import React, {
  useCallback, useEffect, useId, useRef, useState,
} from 'react';
import { Placeholder } from 'react-bootstrap';

import Snackbar from 'components/DesignSystem/Snackbar';
import BlockCard from 'components/Flux/Personalized/MyStudies/BlockCard';
import { formatScenarioName } from 'helper/flux/myStudyBlock';
import useMyStudyScenarios from 'utils/hooks/flux/personalized/useMyStudyScenarios';
import { MyStudyBlock, MyStudyScenario } from 'types/Flux/Personalized/myStudies';

import './index.scss';

export const SCENARIOS_LOADING_MESSAGE = 'Carregando cenários e blocos…';
export const SCENARIOS_ERROR_MESSAGE = 'Não foi possível carregar os cenários.';
export const STUDY_NOT_FOUND_MESSAGE = 'Este estudo não existe mais.';
export const NO_SCENARIOS_MESSAGE = 'Este estudo não tem cenários.';
const NO_BLOCKS_MESSAGE = 'Nenhum bloco neste cenário.';

export interface ScenariosPanelProps {
  /** Id do estudo da linha aberta: a chave da consulta `getMyStudyScenarios` (#1993). */
  studyId: number;
  /** Nome do estudo, para o nome da região ("Cenários e blocos de <estudo>"). */
  studyName: string;
}

/**
 * Skeleton do painel (.dc.html:320-326): título de 120×12 e barras de 30 px, em 2 cenários.
 * Decorativo: quem anuncia o carregando é a região viva do painel.
 */
function ScenariosSkeleton() {
  return (
    <Placeholder
      as="div"
      animation="glow"
      className="my-studies-scenarios-skeleton"
      aria-hidden="true"
      data-testid="my-studies-scenarios-skeleton"
    >
      <Placeholder className="my-studies-scenarios-skeleton-title" />
      <Placeholder className="my-studies-scenarios-skeleton-bar" />
      <Placeholder className="my-studies-scenarios-skeleton-bar" />
      <Placeholder className="my-studies-scenarios-skeleton-title" />
      <Placeholder className="my-studies-scenarios-skeleton-bar" />
    </Placeholder>
  );
}

/**
 * Um cenário (.dc.html:330-345): título em caixa-alta e a grade dos blocos. É um grupo com o
 * nome do cenário ("Cenário sem nome" quando vem vazio); os blocos são uma lista, com `key` pelo
 * id (o início pode repetir).
 */
function ScenarioSection({ scenario, memberLabelOf }: {
  scenario: MyStudyScenario;
  memberLabelOf: (block: MyStudyBlock) => string | null;
}) {
  const titleId = useId();

  return (
    <div className="my-studies-scenario" role="group" aria-labelledby={titleId}>
      <div id={titleId} className="my-studies-scenario-title">{formatScenarioName(scenario.name)}</div>
      {scenario.blocks.length > 0 ? (
        <ul className="my-studies-scenario-blocks">
          {scenario.blocks.map((block, index) => (
            <li key={block.id}>
              <BlockCard block={block} position={index + 1} memberLabel={memberLabelOf(block)} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="my-studies-scenarios-note">{NO_BLOCKS_MESSAGE}</p>
      )}
    </div>
  );
}

/**
 * Painel de cenários e blocos da linha aberta de Meus Estudos (#2002, .dc.html:318-348). A casca
 * o monta pelo `renderExpanded` da `StudyRow` (#1998), então ele só existe com a linha aberta, e
 * só então pede os cenários (`useMyStudyScenarios`). Entra sem moldura: o separador, o fundo e o
 * padding são da linha.
 *
 * Estados: skeleton (1ª carga, "Tentar de novo" e reabertura depois de um erro, que refaz a
 * consulta), erro local com "Tentar de novo", estudo que não existe mais (404; a lista é invalidada
 * na camada da API) e os cenários. Reabrir o mesmo estudo em até 30 min já mostra os cenários do
 * cache, sem skeleton; o erro antigo em cache nunca aparece.
 *
 * Acessibilidade:
 * - o painel é uma região com nome ("Cenários e blocos de <estudo>"), focável só por programa;
 * - o carregando é anunciado numa região viva sempre montada. Ela nasce vazia e só recebe o texto
 *   num timer depois de montar: uma região que já nasce preenchida costuma não ser lida, e num
 *   clique (update discreto) o efeito roda no mesmo update que insere a região;
 * - os erros vão em `role="alert"`, montados com a falha, como o `ListErrorBlock`;
 * - "Tentar de novo" leva o foco ao painel antes de o botão sumir no carregando: o foco não cai
 *   no `<body>`, como na `ListZone`.
 */
export default function ScenariosPanel({ studyId, studyName }: ScenariosPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    view, scenarios, retry, memberLabelOf,
  } = useMyStudyScenarios(studyId);

  // A região viva monta vazia e recebe o texto numa tarefa seguinte (o timer é limpo ao fechar)
  const [liveRegionReady, setLiveRegionReady] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setLiveRegionReady(true), 0);
    return () => window.clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    panelRef.current?.focus({ preventScroll: true });
    retry();
  }, [retry]);

  return (
    <div
      ref={panelRef}
      className="my-studies-scenarios"
      role="region"
      aria-label={`Cenários e blocos de ${studyName}`}
      tabIndex={-1}
    >
      <div role="status" className="visually-hidden">
        {liveRegionReady && view === 'loading' ? SCENARIOS_LOADING_MESSAGE : ''}
      </div>
      {view === 'loading' && <ScenariosSkeleton />}
      {view === 'error' && (
        <div role="alert">
          <Snackbar variant="danger" actionText="Tentar de novo" onAction={handleRetry}>
            {SCENARIOS_ERROR_MESSAGE}
          </Snackbar>
        </div>
      )}
      {view === 'not-found' && (
        <div role="alert">
          <Snackbar variant="warning">{STUDY_NOT_FOUND_MESSAGE}</Snackbar>
        </div>
      )}
      {view === 'ready' && scenarios.length === 0 && (
        <p className="my-studies-scenarios-note">{NO_SCENARIOS_MESSAGE}</p>
      )}
      {view === 'ready' && scenarios.map((scenario) => (
        <ScenarioSection key={scenario.id} scenario={scenario} memberLabelOf={memberLabelOf} />
      ))}
    </div>
  );
}
